import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'

import { useToast } from '@/hooks'
import { TrialProjectLimitError } from '@/modules/errors'
import {
  type Project,
  useProjects,
  useProjectsLoaders,
  useProjectsMutating,
  useProjectsMutations,
} from '@/modules/projects'
import { captureException } from '@/services/monitoring/captureException'
import { getProjectPath } from '@/utils'

// The trial allows one case, and creating one is not idempotent: a reload or a second tab
// must land on the case that already exists instead of failing.
const useGoToExistingCase = (goToCase: (project: Project) => void) => {
  const [existingCase] = useProjects()
  const { fetchProjects } = useProjectsLoaders()
  const [hasLookedUp, setHasLookedUp] = useState(false)
  const lookUpCases = useCallback(
    () =>
      fetchProjects(null, { allFolders: true }).then(() =>
        setHasLookedUp(true),
      ),
    [fetchProjects],
  )

  useEffect(() => {
    lookUpCases().catch(captureException)
  }, [lookUpCases])

  useEffect(() => {
    if (hasLookedUp && existingCase) goToCase(existingCase)
  }, [hasLookedUp, existingCase, goToCase])

  return lookUpCases
}

export const useOpenTrialCase = () => {
  const navigate = useNavigate()
  const { errorToast } = useToast()
  const { createProject } = useProjectsMutations()
  const isOpening = useProjectsMutating()
  const goToCase = useCallback(
    (project: Project) => navigate({ to: `${getProjectPath(project)}/files` }),
    [navigate],
  )
  const lookUpCases = useGoToExistingCase(goToCase)

  const handleFailure = (error: Error) => {
    if (error instanceof TrialProjectLimitError) {
      lookUpCases().catch(captureException)
      return
    }
    errorToast('We could not open the case', error)
  }

  const openCase = (name: string) =>
    createProject({ name }).then(goToCase).catch(handleFailure)

  return { openCase, isOpening }
}
